// The one place that tears down user-scoped client state, so signing out (or
// switching accounts) can't leak anything into the next session.
//
// The app is an SPA: signing out and signing in as another account never
// reloads the page, so every piece of user-scoped state survives the switch by
// default. The query cache is the dangerous one - ['me'] is not keyed by user,
// so the new account renders against the previous account's memberships and
// RequireWorkspace waves through a workspace id the new user has no membership
// in (FORBIDDEN "no active membership for workspace ...").
//
// Registered once at startup from main.tsx, before anything renders. Anything
// new that caches per-user data belongs in the teardown below.
//
// Deliberately NOT cleared: theme, locale and sidebar prefs belong to the
// browser rather than the account (and locale re-syncs from the profile on
// load), and the wf-active-workspace:<userId> / wf-active-location:<userId>:*
// keys are already namespaced per user, so they can't cross accounts.
import { supabase } from './supabase';
import { queryClient } from './query-client';
import { Sentry } from './sentry';
import { useWorkspaceStore } from '@/stores/workspace';
import { useLocationStore } from '@/stores/location';
import { useUpgradeDialogStore } from '@/stores/upgradeDialog';
import { useTourStore } from '@/features/tour/store';

// "Already shown to the signed-in user this browser session" markers.
const SESSION_FLAGS = ['wf-upgrade-dialog-shown', 'wf-start-tour'];

// undefined = the INITIAL_SESSION event hasn't landed yet. That first event
// only records who is signed in - nothing is cached at startup, so there is
// nothing to tear down.
let currentUserId: string | null | undefined = undefined;

supabase.auth.onAuthStateChange((_event, session) => {
  const nextUserId = session?.user.id ?? null;
  // Token refreshes and repeat SIGNED_IN events for the same person must not
  // wipe a live cache, so the identity - not the event name - is the trigger.
  if (nextUserId === currentUserId) return;
  const isFirstEvent = currentUserId === undefined;
  currentUserId = nextUserId;

  if (!isFirstEvent) {
    queryClient.clear();
    useWorkspaceStore.getState().reset();
    useLocationStore.getState().reset();
    useUpgradeDialogStore.getState().setOpen(false);
    useTourStore.getState().endTour(false);
    for (const key of SESSION_FLAGS) sessionStorage.removeItem(key);
    // Breadcrumbs are a global Sentry scope: without this, the previous user's
    // navigation trail rides along on the next user's error reports.
    Sentry.getCurrentScope().clearBreadcrumbs();
  }

  // Runs on the first event too: a cold page load can also be a new account
  // taking over a browser where someone else's tour progress is persisted.
  if (nextUserId) useTourStore.getState().claimForUser(nextUserId);
});
